/**
 * Niveaux d'action et droits par rôle.
 *
 * Le niveau d'un service est déclaré dans son code, jamais déduit à
 * l'exécution : c'est ce qui permet de répondre à « qu'est-ce que l'agent
 * peut faire tout seul ? » en lisant la définition, sans dérouler le code.
 */
import type { Role } from "./context.js";

export const ActionLevel = {
  /** Consultation, synthèse, recherche. Exécution automatique. */
  Read: "N1",
  /**
   * Écriture interne réversible : brouillon de devis, note de chantier,
   * rendez-vous, tâche. Automatique, journalisée, annulable depuis l'app.
   */
  ReversibleWrite: "N2",
  /**
   * Effet hors de l'entreprise ou irréversible : e-mail client, commande
   * fournisseur, envoi de devis ou de facture, engagement de prix,
   * suppression. Confirmation humaine explicite obligatoire.
   */
  ExternalEffect: "N3",
} as const;

export type ActionLevel = (typeof ActionLevel)[keyof typeof ActionLevel];

/**
 * Hiérarchie des rôles. `lecture_seule` et `ouvrier` ne sont pas comparables
 * sur une seule échelle — un ouvrier écrit sur le terrain, un comptable ne
 * doit rien écrire du tout — donc on décrit ce que chaque rôle peut faire
 * plutôt que de les ordonner.
 */
const CAPABILITIES: Record<Role, readonly ActionLevel[]> = {
  patron: ["N1", "N2", "N3"],
  conducteur: ["N1", "N2"],
  assistant: ["N1", "N2"],
  chef_de_chantier: ["N1", "N2"],
  ouvrier: ["N1", "N2"],
  lecture_seule: ["N1"],
};

/**
 * Rôle de terrain : l'ouvrier et le chef de chantier. Le pendant exact de
 * `public.is_field_only()` en base — l'écran s'en sert pour choisir le menu
 * et « Ma journée », la base pour fermer le commercial.
 */
export function isFieldRole(role: Role | null | undefined): boolean {
  return role === "ouvrier" || role === "chef_de_chantier";
}

export function roleAllows(role: Role, level: ActionLevel): boolean {
  return CAPABILITIES[role].includes(level);
}

/**
 * Seul le patron engage l'entreprise. Un conducteur prépare un devis, il ne
 * l'envoie pas — c'est la règle métier, pas une limite technique.
 */
export function rolesForLevel(level: ActionLevel): Role[] {
  return (Object.keys(CAPABILITIES) as Role[]).filter((r) =>
    roleAllows(r, level),
  );
}
